import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import OrderHeader from './components/OrderHeader';
import StatusTimeline from './components/StatusTimeline';
import VehicleDetailsCard from './components/VehicleDetailsCard';
import DealershipInfoCard from './components/DealershipInfoCard';
import ServiceItemsCard from './components/ServiceItemsCard';
import TechnicianAssignmentCard from './components/TechnicianAssignmentCard';
import WorkNotesCard from './components/WorkNotesCard';
import FileAttachmentsCard from './components/FileAttachmentsCard';
import AuditLogCard from './components/AuditLogCard';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const OrderDetailsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams?.get('id') || '12345';

  const [order, setOrder] = useState(null);
  const [isEditingVehicle, setIsEditingVehicle] = useState(false);
  const [isEditingService, setIsEditingService] = useState(false);
  const [loading, setLoading] = useState(true);

  // Mock data
  const mockOrder = {
    id: orderId,
    orderNumber: `ORD-${orderId}`,
    status: 'IN_PROGRESS',
    priority: 'HIGH',
    createdAt: '2025-08-28T10:30:00Z',
    updatedAt: '2025-08-30T14:15:00Z',
    vehicle: {
      vin: 'WBAVA31070NL12345',
      make: 'BMW',
      model: '3er Touring',
      year: 2023,
      mileage: '45000',
      licensePlate: 'K-BM 1234',
      notes: 'Kundenfahrzeug mit Vollausstattung. Regelmäßige Wartung beim Autohaus.',
      imageUrl: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&h=300&fit=crop',
      vinPhotoUrl: 'https://images.pexels.com/photos/3806288/pexels-photo-3806288.jpeg?w=400&h=300&fit=crop'
    },
    dealership: {
      id: 'dealer_001',
      name: 'BMW Autohaus Köln',
      brand: 'BMW',
      description: 'Autorisierter BMW Partner mit Vollservice',
      contactPerson: 'Herr Michael Schmidt',
      phone: '+49 221 123456',
      email: 'service@bmw-koeln.de',
      address: {
        street: 'Aachener Straße 123',
        zipCode: '50931',
        city: 'Köln'
      },
      vatId: 'DE123456789',
      paymentTerms: 30,
      isActive: true
    },
    serviceItems: [
      {
        id: 1,
        serviceType: 'oil_change',
        description: 'Motorölwechsel mit BMW Original Öl 5W-30',
        quantity: 1,
        price: 89.50,
        unit: 'Stück'
      },
      {
        id: 2,
        serviceType: 'inspection',
        description: 'Große Inspektion nach BMW Service-Intervall',
        quantity: 1,
        price: 245.00,
        unit: 'Stück'
      },
      {
        id: 3,
        serviceType: 'brake_service',
        description: 'Bremsbeläge vorne erneuern',
        quantity: 1,
        price: 180.00,
        unit: 'Satz'
      }
    ],
    assignment: {
      technician: {
        id: 'tech_001',
        name: 'Thomas Müller',
        specialization: 'BMW Spezialist',
        phone: '+49 221 987654',
        email: 'thomas.mueller@service.de',
        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
        status: 'AVAILABLE',
        completedJobs: 156,
        rating: 4.8
      },
      assignedAt: '2025-08-29T09:15:00Z',
      scheduledStart: '2025-08-30T08:00:00Z'
    },
    workNotes: [
      {
        id: 1,
        content: `Fahrzeug in Werkstatt eingetroffen. Sichtprüfung durchgeführt.\nKeine äußeren Schäden festgestellt. Kilometerstand bestätigt.`,
        author: 'Thomas Müller',
        authorId: 'tech_001',
        timestamp: '2025-08-30T08:30:00Z',
        type: 'WORK_NOTE'
      },
      {
        id: 2,
        content: 'Motorölwechsel abgeschlossen. BMW Original Öl 5W-30 verwendet. Ölfilter erneuert.',
        author: 'Thomas Müller',
        authorId: 'tech_001',
        timestamp: '2025-08-30T10:15:00Z',
        type: 'WORK_NOTE'
      },
      {
        id: 3,
        content: `Große Inspektion begonnen:\n- Bremsen geprüft: Beläge vorne verschlissen\n- Reifen: Profiltiefe OK\n- Beleuchtung: Funktionsfähig`,
        author: 'Thomas Müller',
        authorId: 'tech_001',
        timestamp: '2025-08-30T12:45:00Z',
        type: 'WORK_NOTE'
      }
    ],
    attachments: [
      {
        id: 1,
        name: 'VIN_Foto_BMW_12345.jpg',
        size: 2048576,
        type: 'image/jpeg',
        uploadedAt: '2025-08-28T10:35:00Z',
        uploadedBy: 'System',
        url: 'https://images.pexels.com/photos/3806288/pexels-photo-3806288.jpeg?w=400&h=300&fit=crop'
      },
      {
        id: 2,
        name: 'Fahrzeugschein_BMW.pdf',
        size: 1024000,
        type: 'application/pdf',
        uploadedAt: '2025-08-28T11:00:00Z',
        uploadedBy: 'Herr Schmidt',
        url: '#'
      },
      {
        id: 3,
        name: 'Wartungshistorie.xlsx',
        size: 512000,
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        uploadedAt: '2025-08-29T14:20:00Z',
        uploadedBy: 'Thomas Müller',
        url: '#'
      }
    ]
  };

  const mockStatusHistory = [
    { status: 'NEW', timestamp: '2025-08-28T10:30:00Z', user: 'System' },
    { status: 'IN_REVIEW', timestamp: '2025-08-28T11:15:00Z', user: 'Herr Schmidt' },
    { status: 'ASSIGNED', timestamp: '2025-08-29T09:15:00Z', user: 'Dispatcher' },
    { status: 'IN_PROGRESS', timestamp: '2025-08-30T08:30:00Z', user: 'Thomas Müller' }
  ];

  const mockAvailableTechnicians = [
    {
      id: 'tech_002',
      name: 'Andreas Weber',
      specialization: 'Allgemeine Reparaturen',
      phone: '+49 221 555001',
      avatar: 'https://randomuser.me/api/portraits/men/45.jpg',
      status: 'AVAILABLE',
      completedJobs: 89,
      rating: 4.6
    },
    {
      id: 'tech_003',
      name: 'Stefan Klein',
      specialization: 'Elektronik & Diagnose',
      phone: '+49 221 555002',
      avatar: 'https://randomuser.me/api/portraits/men/28.jpg',
      status: 'AVAILABLE',
      completedJobs: 134,
      rating: 4.9
    },
    {
      id: 'tech_004',
      name: 'Marcus Fischer',
      specialization: 'Motor & Getriebe',
      phone: '+49 221 555003',
      avatar: 'https://randomuser.me/api/portraits/men/38.jpg',
      status: 'BUSY',
      completedJobs: 201,
      rating: 4.7
    }
  ];

  const mockAuditLog = [
    {
      id: 1,
      action: 'CREATED',
      details: 'Auftrag erstellt',
      timestamp: '2025-08-28T10:30:00Z',
      userName: 'System',
      userId: 'system',
      ipAddress: '192.168.1.100'
    },
    {
      id: 2,
      action: 'STATUS_CHANGED',
      details: 'Status von NEW zu IN_REVIEW',
      timestamp: '2025-08-28T11:15:00Z',
      userName: 'Herr Schmidt',
      userId: 'user_001',
      ipAddress: '192.168.1.101',
      changes: {
        status: { from: 'NEW', to: 'IN_REVIEW' }
      }
    },
    {
      id: 3,
      action: 'FILE_UPLOADED',
      details: 'Fahrzeugschein_BMW.pdf hochgeladen',
      timestamp: '2025-08-28T11:00:00Z',
      userName: 'Herr Schmidt',
      userId: 'user_001',
      ipAddress: '192.168.1.101'
    },
    {
      id: 4,
      action: 'ASSIGNED',
      details: 'Techniker Thomas Müller zugewiesen',
      timestamp: '2025-08-29T09:15:00Z',
      userName: 'Dispatcher',
      userId: 'dispatcher_001',
      ipAddress: '192.168.1.102'
    },
    {
      id: 5,
      action: 'STATUS_CHANGED',
      details: 'Status von ASSIGNED zu IN_PROGRESS',
      timestamp: '2025-08-30T08:30:00Z',
      userName: 'Thomas Müller',
      userId: 'tech_001',
      ipAddress: '192.168.1.103',
      changes: {
        status: { from: 'ASSIGNED', to: 'IN_PROGRESS' }
      }
    },
    {
      id: 6,
      action: 'NOTE_ADDED',
      details: 'Arbeitsnotiz hinzugefügt',
      timestamp: '2025-08-30T10:15:00Z',
      userName: 'Thomas Müller',
      userId: 'tech_001',
      ipAddress: '192.168.1.103'
    }
  ];

  const currentUser = {
    id: 'dispatcher_001',
    name: 'Dispatcher',
    role: 'dispatcher'
  };

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setOrder(mockOrder);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [orderId]);

  const handleStatusChange = (newStatus) => {
    setOrder(prev => ({
      ...prev,
      status: newStatus,
      updatedAt: new Date()?.toISOString()
    }));
  };

  const handleVehicleUpdate = (updatedData) => {
    setOrder(prev => ({
      ...prev,
      vehicle: { ...prev?.vehicle, ...updatedData },
      updatedAt: new Date()?.toISOString()
    }));
  };

  const handleServiceItemsUpdate = (updatedItems) => {
    setOrder(prev => ({
      ...prev,
      serviceItems: updatedItems,
      updatedAt: new Date()?.toISOString()
    }));
  };

  const handleTechnicianAssign = (technician) => {
    setOrder(prev => ({
      ...prev,
      assignment: {
        technician,
        assignedAt: new Date()?.toISOString(),
        scheduledStart: null
      },
      updatedAt: new Date()?.toISOString()
    }));
  };

  const handleTechnicianUnassign = () => {
    setOrder(prev => ({
      ...prev,
      assignment: { technician: null },
      updatedAt: new Date()?.toISOString()
    }));
  };

  const handleAddWorkNote = (note) => {
    setOrder(prev => ({
      ...prev,
      workNotes: [note, ...prev?.workNotes],
      updatedAt: new Date()?.toISOString()
    }));
  };

  const handleFileUpload = (file) => {
    setOrder(prev => ({
      ...prev,
      attachments: [...prev?.attachments, file],
      updatedAt: new Date()?.toISOString()
    }));
  };

  const handleFileDelete = (fileId) => {
    setOrder(prev => ({
      ...prev,
      attachments: prev?.attachments?.filter(file => file?.id !== fileId),
      updatedAt: new Date()?.toISOString()
    }));
  };

  const handleFileView = (file) => {
    window.open(file?.url, '_blank');
  };

  const handleEdit = () => {
    // Navigate to edit mode or show edit modal
  };

  const handleInvoice = () => {
    // Generate invoice
    handleStatusChange('INVOICED');
  };

  const handleContact = () => {
    // Open contact modal or navigate to contact page
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="admin" />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="Loader2" size={32} className="animate-spin mx-auto mb-4 text-primary" />
                <p className="text-text-secondary">Auftrag wird geladen...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="admin" />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center py-16">
              <Icon name="AlertTriangle" size={48} className="mx-auto mb-4 text-error" />
              <h2 className="text-2xl font-bold text-text-primary mb-2">Auftrag nicht gefunden</h2>
              <p className="text-text-secondary mb-6">
                Der angeforderte Auftrag konnte nicht gefunden werden.
              </p>
              <Button
                variant="default"
                onClick={() => navigate('/order-management-dashboard')}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Zurück zur Übersicht
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          <OrderHeader
            order={order}
            onStatusChange={handleStatusChange}
            onEdit={handleEdit}
            onInvoice={handleInvoice}
          />

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="xl:col-span-2 space-y-6">
              <VehicleDetailsCard
                vehicle={order?.vehicle}
                onUpdate={handleVehicleUpdate}
                isEditing={isEditingVehicle}
                onToggleEdit={() => setIsEditingVehicle(!isEditingVehicle)}
              />

              <DealershipInfoCard
                dealership={order?.dealership}
                onContact={handleContact}
              />

              <ServiceItemsCard
                serviceItems={order?.serviceItems}
                onUpdate={handleServiceItemsUpdate}
                isEditing={isEditingService}
                onToggleEdit={() => setIsEditingService(!isEditingService)}
              />

              <TechnicianAssignmentCard
                assignment={order?.assignment}
                availableTechnicians={mockAvailableTechnicians}
                onAssign={handleTechnicianAssign}
                onUnassign={handleTechnicianUnassign}
              />

              <WorkNotesCard
                workNotes={order?.workNotes}
                onAddNote={handleAddWorkNote}
                currentUser={currentUser}
              />

              <FileAttachmentsCard
                attachments={order?.attachments}
                onUpload={handleFileUpload}
                onDelete={handleFileDelete}
                onView={handleFileView}
              />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <StatusTimeline
                order={order}
                statusHistory={mockStatusHistory}
              />

              <AuditLogCard
                auditLog={mockAuditLog}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsPage;